import apiClient, { getAccessToken } from './client';
import { resolveShopSlugFromUrl } from '../utils/shopSlug';
import type { ApiResponse, ChatMessage } from '../types';

// `/vendor/support/conversations` now serves the real transcript from
// `chatbot_conversations` — a list of conversations, each with its own
// `messages` array, plus `current_conversation_id` and a flattened `messages`
// for the most recent thread. It used to reconstruct a pseudo-history out of
// AI scoring logs, which meant the assistant had no memory at all between
// turns (audit 2026-07-28).
interface ConversationRecord {
  conversation_id: string;
  title: string | null;
  status: string;
  message_count: number;
  escalated_at: string | null;
  messages: ChatMessage[];
  created_at: string;
  updated_at: string;
}

interface ConversationsResponse {
  conversations: ConversationRecord[];
  current_conversation_id: string | null;
  messages: ChatMessage[];
}

export const supportApi = {
  async sendMessage(
    content: string,
    conversationId?: string,
  ): Promise<ChatMessage & { conversation_id?: string }> {
    const res = await apiClient.post<
      ApiResponse<ChatMessage & { conversation_id?: string }>
    >('/vendor/support/chat', {
      content,
      conversation_id: conversationId,
    });
    return res.data.data;
  },

  /** The active thread: its messages oldest-first, and the id the next
   *  message must carry to continue it. Both come back from one request —
   *  the caller needs them together, and splitting this into two accessors
   *  would just fetch the same payload twice. */
  async getConversations(): Promise<{
    messages: ChatMessage[];
    conversationId: string | null;
  }> {
    const res = await apiClient.get<ApiResponse<ConversationsResponse>>(
      '/vendor/support/conversations',
    );
    return {
      messages: res.data.data?.messages ?? [],
      conversationId: res.data.data?.current_conversation_id ?? null,
    };
  },

  async escalate(scope: SupportScope, conversationId: string, clientRequestId: string, reason?: string): Promise<SupportCase> {
    const result = await supportRequest(scope, '/escalate', { conversation_id: conversationId,
      client_request_id: clientRequestId, reason });
    const item = decodeSupportCase(result.case);
    if (item.client_request_id !== clientRequestId || !item.replay_protection ||
        result.ticket?.conversation_id !== conversationId || result.ticket?.case_id !== item.id ||
        result.ticket?.id !== item.reference) throw new SupportFailure(false);
    return item;
  },
};

export interface SupportCase {
  id: string; reference: string; vendor_id: string; subject: string; category: string;
  owner: 'marketplace_administrators'; sla_hours: null; status: 'open' | 'resolved' | 'closed';
  version: number; source: 'portal_ticket' | 'ai_chat_escalation' | 'marketplace_exception'; client_request_id: string | null;
  replay_protection: boolean; submitted_at: string; updated_at: string;
}
export interface CaseMessage {
  id: string; client_request_id: string; actor_type: 'vendor' | 'admin'; kind: 'create' | 'reply' | 'status';
  body: string; status: SupportCase['status']; version: number; created_at: string;
}
export interface CaseDetail {
  case: SupportCase; messages: CaseMessage[]; has_more: boolean; next_version: number | null;
  source_context: { conversation_id: string; snapshot: string;
    messages: { role: string; content: string; truncated: boolean }[] } | null;
}
export interface SupportScope { readonly token: string; readonly base: string; readonly slug: string; readonly key: string }
let generation = 0;
if (typeof window !== 'undefined') window.addEventListener('vendoriq:token-rotated', () => { generation += 1; });
export function captureSupportScope(): SupportScope | null {
  const token = getAccessToken(), slug = resolveShopSlugFromUrl();
  if (!token || !slug) return null;
  const base = String(apiClient.defaults.baseURL || '/api/v1').replace(/\/+$/, '');
  return Object.freeze({ token, base, slug, key: generation + ':' + slug });
}
export function supportScopeCurrent(scope: SupportScope): boolean {
  return scope.token === getAccessToken() && scope.slug === resolveShopSlugFromUrl() &&
    scope.base === String(apiClient.defaults.baseURL || '/api/v1').replace(/\/+$/, '') &&
    scope.key === generation + ':' + scope.slug;
}
export class SupportFailure extends Error {
  constructor(readonly definite: boolean) { super(definite ? 'Support request was not accepted.' : 'Support outcome is unconfirmed. Check the case queue before submitting again.'); }
}
const object = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
const uuid = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const positive = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v > 0;
export function decodeSupportCase(value: unknown): SupportCase {
  if (!object(value) || !uuid(value.id) || value.reference !== 'VIQ-' + value.id.replace(/-/g, '').toUpperCase() ||
      !uuid(value.vendor_id) || value.owner !== 'marketplace_administrators' || value.sla_hours !== null ||
      !['open', 'resolved', 'closed'].includes(value.status) || !positive(value.version) ||
      !['portal_ticket', 'ai_chat_escalation', 'marketplace_exception'].includes(value.source) || typeof value.replay_protection !== 'boolean' ||
      (value.source === 'marketplace_exception' && value.replay_protection !== false) ||
      (value.replay_protection ? !uuid(value.client_request_id) : value.client_request_id !== null) ||
      !['subject', 'category', 'submitted_at', 'updated_at'].every(k => typeof value[k] === 'string')) throw new SupportFailure(false);
  return value as SupportCase;
}
function decodeMessage(value: unknown): CaseMessage {
  if (!object(value) || !uuid(value.id) || !uuid(value.client_request_id) || !positive(value.version) ||
      !['vendor', 'admin'].includes(value.actor_type) || !['create', 'reply', 'status'].includes(value.kind) ||
      !['open', 'resolved', 'closed'].includes(value.status) || typeof value.body !== 'string' ||
      typeof value.created_at !== 'string') throw new SupportFailure(false);
  return value as CaseMessage;
}
async function supportRequest(scope: SupportScope, path: string, body?: unknown): Promise<Record<string, any>> {
  if (!supportScopeCurrent(scope)) throw new SupportFailure(true);
  const controller = new AbortController();
  const changed = () => { if (!supportScopeCurrent(scope)) controller.abort(); };
  window.addEventListener('vendoriq:token-rotated', changed);
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const operation = async () => {
      if (!supportScopeCurrent(scope) || controller.signal.aborted) throw new SupportFailure(true);
      const csrf = document.cookie.match(/(?:^|; )vendoriq_csrf=([^;]+)/)?.[1];
      const response = await fetch(scope.base + '/vendor/support' + path, {
        method: body === undefined ? 'GET' : 'POST', signal: controller.signal, credentials: 'include', redirect: 'error',
        headers: { Authorization: 'Bearer ' + scope.token, Accept: 'application/json',
          ...(body === undefined ? {} : { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': decodeURIComponent(csrf) } : {}) }) },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
      if (!supportScopeCurrent(scope)) throw new SupportFailure(false);
      if (!response.ok) throw new SupportFailure([400, 401, 403, 404, 409, 422, 429].includes(response.status));
      // Bound bytes while consuming the stream, before a complete response can
      // allocate unbounded text. Eight MiB fits the full 100-message page plus
      // ten context turns even with four-byte Unicode; no receipt truncation.
      if (!response.body) throw new SupportFailure(false);
      const reader = response.body.getReader(), decoder = new TextDecoder('utf-8', { fatal: true });
      const chunks: string[] = [];
      let bytes = 0;
      try {
        while (true) {
          const chunk = await reader.read();
          if (!supportScopeCurrent(scope) || controller.signal.aborted) throw new SupportFailure(false);
          if (chunk.done) break;
          bytes += chunk.value.byteLength;
          if (bytes > 8 * 1024 * 1024) throw new SupportFailure(false);
          chunks.push(decoder.decode(chunk.value, { stream: true }));
        }
        chunks.push(decoder.decode());
      } finally { try { await reader.cancel(); } catch { /* No resend on cancellation failure. */ } reader.releaseLock(); }
      const payload: unknown = JSON.parse(chunks.join(''));
      if (!object(payload) || payload.success !== true || !object(payload.data)) throw new SupportFailure(false);
      return payload.data;
    };
    return await Promise.race([operation(), new Promise<never>((_, reject) => {
      timer = setTimeout(() => { controller.abort(); reject(new SupportFailure(false)); }, 15000);
    })]);
  } catch (error) { throw error instanceof SupportFailure ? error : new SupportFailure(false); }
  finally { clearTimeout(timer); controller.abort(); window.removeEventListener('vendoriq:token-rotated', changed); }
}

// One process-local uncertain operation per current session scope. No durable
// recovery claim, no body/token retention, no automatic resend on remount.
export type CaseAttempt = { requestId: string; caseId?: string; phase: 'pending' | 'unknown' | 'confirmed' | 'rejected'; case?: SupportCase };
const attempts = new Map<string, CaseAttempt>();
export const currentCaseAttempt = (scope: SupportScope) => attempts.get(scope.key);
export function beginCaseAttempt(scope: SupportScope, caseId?: string): CaseAttempt | null {
  const prior = attempts.get(scope.key);
  if (!supportScopeCurrent(scope) || prior?.phase === 'pending' || prior?.phase === 'unknown' || attempts.size >= 128 && !prior) return null;
  const next: CaseAttempt = { requestId: crypto.randomUUID(), caseId, phase: 'pending' };
  attempts.set(scope.key, next);
  return next;
}
export function finishCaseAttempt(scope: SupportScope, attempt: CaseAttempt, result: SupportCase | unknown): void {
  if (attempts.get(scope.key) !== attempt) return;
  if (object(result) && 'reference' in result) { attempt.phase = 'confirmed'; attempt.case = decodeSupportCase(result); }
  else attempt.phase = result instanceof SupportFailure && result.definite ? 'rejected' : 'unknown';
}
export const casesApi = {
  async list(scope: SupportScope, offset = 0, requestId?: string) {
    const result = await supportRequest(scope, '/cases?offset=' + offset + (requestId ? '&client_request_id=' + encodeURIComponent(requestId) : ''));
    if (!Array.isArray(result.cases) || result.cases.length > 50 || typeof result.has_more !== 'boolean' ||
        (result.has_more ? !positive(result.next_offset) : result.next_offset !== null)) throw new SupportFailure(false);
    const cases: SupportCase[] = result.cases.map(decodeSupportCase);
    return { cases, has_more: result.has_more as boolean, next_offset: result.next_offset as number | null };
  },
  async detail(scope: SupportScope, id: string, after = 0): Promise<CaseDetail> {
    if (!uuid(id)) throw new SupportFailure(true);
    const result = await supportRequest(scope, '/cases/' + id + '?after_version=' + after);
    const item = decodeSupportCase(result.case);
    if (item.id !== id || !Array.isArray(result.messages) || result.messages.length > 100 || typeof result.has_more !== 'boolean' ||
        (result.has_more ? !positive(result.next_version) : result.next_version !== null)) throw new SupportFailure(false);
    const messages: CaseMessage[] = result.messages.map(decodeMessage);
    if (messages.some((entry, index) => entry.version <= (index ? messages[index - 1].version : after)) ||
        result.source_context !== null && (!object(result.source_context) || !Array.isArray(result.source_context.messages) ||
        result.source_context.messages.length > 10 || result.source_context.messages.some((entry: unknown) => !object(entry) ||
          !['user', 'assistant'].includes(entry.role) || typeof entry.content !== 'string' || typeof entry.truncated !== 'boolean'))) throw new SupportFailure(false);
    return { ...result, case: item, messages } as CaseDetail;
  },
  async create(scope: SupportScope, requestId: string, subject: string, message: string): Promise<SupportCase> {
    const result = await supportRequest(scope, '/ticket', { client_request_id: requestId, subject, message, category: 'other' });
    const item = decodeSupportCase(result.case);
    if (item.client_request_id !== requestId || !item.replay_protection) throw new SupportFailure(false);
    return item;
  },
  async reply(scope: SupportScope, item: SupportCase, requestId: string, body: string): Promise<SupportCase> {
    const result = await supportRequest(scope, '/cases/' + item.id + '/messages', { client_request_id: requestId,
      expected_version: item.version, kind: 'reply', body });
    const receipt = decodeMessage(result.receipt), updated = decodeSupportCase(result.case);
    if (updated.id !== item.id || receipt.client_request_id !== requestId || receipt.kind !== 'reply' || receipt.actor_type !== 'vendor' ||
        receipt.body !== body.trim() || receipt.version !== item.version + 1) throw new SupportFailure(false);
    return updated;
  },
  async reconcile(scope: SupportScope, attempt: CaseAttempt): Promise<SupportCase | null> {
    let item: SupportCase | null;
    if (attempt.caseId) {
      const result = await supportRequest(scope, '/cases/' + attempt.caseId + '/requests/' + attempt.requestId);
      const receipt = decodeMessage(result.receipt);
      item = decodeSupportCase(result.case);
      if (item.id !== attempt.caseId || receipt.client_request_id !== attempt.requestId || receipt.actor_type !== 'vendor') throw new SupportFailure(false);
    } else {
      const result = await casesApi.list(scope, 0, attempt.requestId);
      item = result.cases.find(c => c.client_request_id === attempt.requestId && c.replay_protection) ?? null;
    }
    // The view must also match vendor_id before adopting this receipt.
    return item;
  },
};

// ── NS-03: shopper support cases forwarded to THIS vendor (read only) ───────
//
// A sibling channel, not the same table as `casesApi` above -- see
// `app/models/shopper_support_case.py`. The marketplace admin owns creation,
// reply and forwarding; the vendor portal only ever reads cases the admin
// has explicitly forwarded (`GET /vendor/support/shopper-cases*`, scoped
// server-side to `forwarded_vendor_id == this vendor` -- never filtered
// client-side).

export interface ShopperSupportCase {
  id: string; reference: string; shopify_order_id: string; subject: string; category: string;
  owner: 'marketplace_administrators'; status: 'open' | 'resolved' | 'closed'; version: number;
  client_request_id: string | null; replay_protection: boolean; submitted_at: string; updated_at: string;
  forwarded_vendor_id: string | null; forwarded_vendor_name: string | null; forwarded_at: string | null;
}
export interface ShopperCaseMessage {
  id: string; client_request_id: string; actor_type: 'shopper' | 'admin'; kind: 'create' | 'reply' | 'status' | 'forward';
  body: string; status: ShopperSupportCase['status']; version: number; created_at: string;
}
export interface ShopperCaseDetail {
  case: ShopperSupportCase; messages: ShopperCaseMessage[]; has_more: boolean; next_version: number | null;
}
function decodeShopperCase(value: unknown): ShopperSupportCase {
  if (!object(value) || !uuid(value.id) || value.reference !== 'VIQ-SH-' + value.id.replace(/-/g, '').toUpperCase() ||
      typeof value.shopify_order_id !== 'string' || !/^[1-9][0-9]{0,19}$/.test(value.shopify_order_id) ||
      value.owner !== 'marketplace_administrators' || !['open', 'resolved', 'closed'].includes(value.status) ||
      !positive(value.version) || typeof value.replay_protection !== 'boolean' ||
      (value.replay_protection ? !uuid(value.client_request_id) : value.client_request_id !== null) ||
      (value.forwarded_vendor_id !== null && !uuid(value.forwarded_vendor_id)) ||
      // forwarded_vendor_id/forwarded_at are NOT coupled -- a vendor deleted after being
      // forwarded to leaves forwarded_vendor_id null (ON DELETE SET NULL) while forwarded_at
      // remains as the durable "this case WAS forwarded" receipt. See
      // app/models/shopper_support_case.py's docstring for the full reasoning.
      (value.forwarded_at !== null && typeof value.forwarded_at !== 'string') ||
      !['subject', 'category', 'submitted_at', 'updated_at'].every(k => typeof value[k] === 'string')) throw new SupportFailure(false);
  return value as ShopperSupportCase;
}
function decodeShopperMessage(value: unknown): ShopperCaseMessage {
  if (!object(value) || !uuid(value.id) || !uuid(value.client_request_id) || !positive(value.version) ||
      !['shopper', 'admin'].includes(value.actor_type) || !['create', 'reply', 'status', 'forward'].includes(value.kind) ||
      !['open', 'resolved', 'closed'].includes(value.status) || typeof value.body !== 'string' ||
      typeof value.created_at !== 'string') throw new SupportFailure(false);
  return value as ShopperCaseMessage;
}
export const shopperCasesApi = {
  async list(scope: SupportScope, offset = 0): Promise<{ cases: ShopperSupportCase[]; has_more: boolean; next_offset: number | null }> {
    const result = await supportRequest(scope, '/shopper-cases?offset=' + offset);
    if (!Array.isArray(result.cases) || result.cases.length > 50 || typeof result.has_more !== 'boolean' ||
        (result.has_more ? !positive(result.next_offset) : result.next_offset !== null)) throw new SupportFailure(false);
    return { cases: result.cases.map(decodeShopperCase), has_more: result.has_more as boolean, next_offset: result.next_offset as number | null };
  },
  async detail(scope: SupportScope, id: string, after = 0): Promise<ShopperCaseDetail> {
    if (!uuid(id)) throw new SupportFailure(true);
    const result = await supportRequest(scope, '/shopper-cases/' + id + '?after_version=' + after);
    const item = decodeShopperCase(result.case);
    if (item.id !== id || !Array.isArray(result.messages) || result.messages.length > 100 || typeof result.has_more !== 'boolean' ||
        (result.has_more ? !positive(result.next_version) : result.next_version !== null)) throw new SupportFailure(false);
    const messages = result.messages.map(decodeShopperMessage);
    if (messages.some((entry: ShopperCaseMessage, index: number) => entry.version <= (index ? messages[index - 1].version : after)))
      throw new SupportFailure(false);
    return { case: item, messages, has_more: result.has_more, next_version: result.next_version };
  },
};
